/**
 * The design tokens each protocol layout draws with. The renderer reads every
 * size, colour and treatment that differs between layouts from here, so a
 * layout is one record rather than a branch scattered through every section.
 *
 * Page geometry (margins, content width) is deliberately NOT a token: the issue
 * table's column widths are derived from CONTENT_WIDTH and their sum is an
 * invariant the tests walk for every column subset.
 */
import { rgb } from "pdf-lib";
import type { ProtocolLayout } from "@/domains/pm/utils/protocolLayout";

type Color = ReturnType<typeof rgb>;

const BRAND_RED = rgb(0.73, 0.08, 0.17);
const INK = rgb(0.1, 0.11, 0.13);
const NAVY = rgb(0.09, 0.13, 0.2);
const WHITE = rgb(1, 1, 1);
const HEADER_GREY = rgb(0.9, 0.91, 0.92);

export interface ProtocolPdfTheme {
  layout: ProtocolLayout;
  /** Caveats and the text logo fallback. */
  accent: Color;
  /** `letterhead`: logo over a rule. `band`: a full-width coloured band
   *  carrying logo, title and date. `inline`: logo and title on one line. */
  header: "letterhead" | "band" | "inline";
  bandColor: Color;
  titleSize: number;
  titleLineHeight: number;
  /** `panel`: a shaded label/value list. `grid`: two columns of label-over-
   *  value. `inline`: a dense two-column label/value list. */
  metadata: "panel" | "grid" | "inline";
  /** `tiles`: shaded count tiles. `cards`: tiles with a coloured stripe.
   *  `line`: one line of counts. */
  summary: "tiles" | "cards" | "line";
  /** Printed section headings: Summary, Comments, Activity, Attachments. */
  sectionHeadingSize: number;
  sectionHeadingColor: Color;
  /** A short bar under each section heading — `null` for none. */
  sectionRule: Color | null;
  table: {
    headerFill: Color;
    headerText: Color;
    headerHeight: number;
    /** `boxed`: every row outlined. `striped`: alternate rows shaded, no
     *  outline. `ruled`: a hairline under each row. */
    rows: "boxed" | "striped" | "ruled";
    textSize: number;
    lineHeight: number;
    /** Vertical padding a row adds to its lines. */
    padding: number;
    /** From the row's top to its first baseline. */
    baselineOffset: number;
  };
}

/** Today's protocol, unchanged — the default, so existing exports keep their look. */
const CLASSIC: ProtocolPdfTheme = {
  layout: "classic",
  accent: BRAND_RED,
  header: "letterhead",
  bandColor: NAVY,
  titleSize: 22,
  titleLineHeight: 27,
  metadata: "panel",
  summary: "tiles",
  sectionHeadingSize: 14,
  sectionHeadingColor: INK,
  sectionRule: null,
  table: {
    headerFill: HEADER_GREY,
    headerText: rgb(0.4, 0.42, 0.46),
    headerHeight: 23,
    rows: "boxed",
    textSize: 8.25,
    lineHeight: 10.5,
    padding: 9,
    baselineOffset: 13,
  },
};

const MODERN: ProtocolPdfTheme = {
  layout: "modern",
  accent: BRAND_RED,
  header: "band",
  bandColor: NAVY,
  titleSize: 24,
  titleLineHeight: 29,
  metadata: "grid",
  summary: "cards",
  sectionHeadingSize: 14,
  sectionHeadingColor: NAVY,
  sectionRule: BRAND_RED,
  table: {
    headerFill: NAVY,
    headerText: WHITE,
    headerHeight: 24,
    rows: "striped",
    textSize: 8.5,
    lineHeight: 11,
    padding: 11,
    baselineOffset: 14.5,
  },
};

const COMPACT: ProtocolPdfTheme = {
  layout: "compact",
  accent: BRAND_RED,
  header: "inline",
  bandColor: NAVY,
  titleSize: 15,
  titleLineHeight: 19,
  metadata: "inline",
  summary: "line",
  sectionHeadingSize: 11,
  sectionHeadingColor: INK,
  sectionRule: null,
  table: {
    headerFill: HEADER_GREY,
    headerText: rgb(0.4, 0.42, 0.46),
    headerHeight: 18,
    rows: "ruled",
    textSize: 7.5,
    lineHeight: 9.25,
    padding: 6,
    baselineOffset: 10.5,
  },
};

const THEMES: Record<ProtocolLayout, ProtocolPdfTheme> = {
  classic: CLASSIC,
  modern: MODERN,
  compact: COMPACT,
};

export function protocolPdfTheme(layout: ProtocolLayout): ProtocolPdfTheme {
  return THEMES[layout];
}

/** The summary cards' stripe colours, by bucket. */
export const SUMMARY_STRIPES = {
  inProgress: rgb(0.15, 0.39, 0.92),
  resolved: rgb(0.09, 0.6, 0.35),
  closed: rgb(0.55, 0.57, 0.6),
} as const;
